/**
 * Routes, and the two things every page needs: who is signed in, and the
 * obligation-type navigation.
 *
 * Both are fetched once here rather than per page, so moving between matrices
 * doesn't rebuild the sidebar and doesn't re-ask who you are.
 */

import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { api, type AuthState, type ObligationTypeInUse } from './api'
import { ClientDetailPage } from './pages/ClientDetailPage'
import { NewClientPage } from './pages/NewClientPage'
import { ImportClientsPage } from './pages/ImportClientsPage'
import { ClientsPage } from './pages/ClientsPage'
import { DueAndOverduePage } from './pages/DueAndOverduePage'
import { MatrixPage } from './pages/MatrixPage'
import { UserAccessPage } from './pages/UserAccessPage'
import { JobsAndPricesPage } from './pages/JobsAndPricesPage'
import { SuggestionsPage } from './pages/SuggestionsPage'
import { MyWeekPage } from './pages/MyWeekPage'
import { ApprovalsPage } from './pages/ApprovalsPage'
import { SelfApprovedPage } from './pages/SelfApprovedPage'
import { ReportsPage } from './pages/ReportsPage'
import { LateTimesheetsPage } from './pages/LateTimesheetsPage'
import { AwaitingAccessPage, SignInPage } from './pages/SignInPage'

export default function App() {
  const [auth, setAuth] = useState<AuthState | null>(null)
  const [obligationTypes, setObligationTypes] = useState<ObligationTypeInUse[]>([])
  const [fatalError, setFatalError] = useState<string | null>(null)

  // Who am I, first. Everything below depends on the answer, and in cognito
  // mode every other call would 401 until it is known.
  useEffect(() => {
    api
      .me()
      .then(setAuth)
      .catch((error: unknown) =>
        setFatalError(error instanceof Error ? error.message : String(error))
      )
  }, [])

  const signedIn = auth !== null && (auth.mode === 'anonymous' || auth.user?.isActive === true)

  useEffect(() => {
    if (!signedIn) return
    api
      .obligationTypes()
      .then((response) => setObligationTypes(response.obligationTypes))
      .catch((error: unknown) =>
        setFatalError(error instanceof Error ? error.message : String(error))
      )
  }, [signedIn])

  if (fatalError !== null) {
    // The API being unreachable means no page below would render either. Said
    // plainly, with what to check.
    return (
      <div className="mx-auto max-w-[40rem] px-8 py-16">
        <h1 className="text-[28px]">Cannot reach the EQ Practice API</h1>
        <p className="mt-3 text-[14px]">{fatalError}</p>
        <p className="mt-3 text-[12.5px] text-eq-helper">
          The API runs separately from this page in development. Start it with{' '}
          <code className="rounded bg-eq-grey px-1.5 py-0.5">npm run dev</code> in{' '}
          <code className="rounded bg-eq-grey px-1.5 py-0.5">server/</code>, then reload.
        </p>
      </div>
    )
  }

  if (auth === null) {
    return <div className="px-8 py-16 text-[12.5px] text-eq-helper">Loading…</div>
  }

  if (auth.mode === 'cognito') {
    if (auth.user === null) return <SignInPage />
    if (!auth.user.isActive) return <AwaitingAccessPage user={auth.user} />
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<DueAndOverduePage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route
        path="/matrix/:code"
        element={<MatrixPage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route
        path="/clients"
        element={<ClientsPage obligationTypes={obligationTypes} auth={auth} />}
      />
      {/* Before /clients/:id, so "new" and "import" are never read as ids. */}
      <Route
        path="/clients/import"
        element={<ImportClientsPage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route
        path="/clients/new"
        element={<NewClientPage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route
        path="/clients/:id"
        element={<ClientDetailPage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route
        path="/users"
        element={<UserAccessPage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route
        path="/time/jobs-and-prices"
        element={<JobsAndPricesPage obligationTypes={obligationTypes} auth={auth} />}
      />
      <Route path="/time/my-week" element={<MyWeekPage obligationTypes={obligationTypes} auth={auth} />} />
      <Route path="/time/suggestions" element={<SuggestionsPage obligationTypes={obligationTypes} auth={auth} />} />
      <Route path="/time/approvals" element={<ApprovalsPage obligationTypes={obligationTypes} auth={auth} />} />
      <Route path="/time/self-approved" element={<SelfApprovedPage obligationTypes={obligationTypes} auth={auth} />} />
      <Route path="/time/reports" element={<ReportsPage obligationTypes={obligationTypes} auth={auth} />} />
      <Route path="/time/late-timesheets" element={<LateTimesheetsPage obligationTypes={obligationTypes} auth={auth} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
